import { useEffect, useRef, useState } from 'react'
import { X, ArrowUpRight } from 'lucide-react'
import { site } from '@/config/site'
import { ButtonLink } from '@/components/ui/Button'

const STORAGE_KEY = 'gia-summit-lightbox-dismissed'
const OPEN_DELAY_MS = 1800

/**
 * LifeSpan Summit lightbox — registration closes shortly after launch, so
 * the summit gets a one-time (per session) pop-up per Website Review.
 */
export function SummitLightbox() {
  const [open, setOpen] = useState(false)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY)) return
    } catch {
      return // storage unavailable (private mode) — skip rather than nag every render
    }
    const timer = setTimeout(() => setOpen(true), OPEN_DELAY_MS)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!open) return
    closeRef.current?.focus()
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = originalOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function dismiss() {
    setOpen(false)
    try {
      sessionStorage.setItem(STORAGE_KEY, '1')
    } catch {
      // storage unavailable — the dialog simply may reappear next load
    }
  }

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="summit-lightbox-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-6"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close announcement"
        onClick={dismiss}
        className="absolute inset-0 bg-ink-deep/70"
      />

      <div className="relative w-full max-w-lg bg-surface p-8 shadow-[0_40px_90px_-30px_rgb(44_41_38_/_0.45)] sm:p-10">
        <button
          ref={closeRef}
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="absolute right-4 top-4 p-2 text-ink-deep hover:bg-surface-alt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>

        <span className="eyebrow text-brand-strong">LifeSpan Summit</span>
        <h2
          id="summit-lightbox-title"
          className="mt-4 font-display text-3xl font-extrabold leading-tight text-ink-deep sm:text-4xl"
        >
          Registration is open — for a short window.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-ink-slate">
          The LifeSpan Summit brings leaders from business, policy, science,
          and philanthropy into the same room to design for longer lives.
          Seats are limited and registration closes soon.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <ButtonLink
            href={site.summitUrl}
            variant="primary"
            size="lg"
            target="_blank"
            rel="noopener noreferrer"
          >
            Register now
            <ArrowUpRight className="h-4 w-4" aria-hidden />
          </ButtonLink>
          <button
            type="button"
            onClick={dismiss}
            className="text-sm font-bold uppercase tracking-[0.08em] text-ink-muted hover:text-ink-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  )
}
